import Alert from "@amzn/meridian/alert";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Input from "@amzn/meridian/input";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { useEffect, useRef, useState } from "react";
import LoadingSpinner from "src/components/shared/loadingSpinner/loadingSpinner";
import adminReportService from "../service/adminReportService";
import { QuestionPreview as Preview, RegionCode } from "../types";
import QuestionPreview from "./questionPreview";

interface TestCaseModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * The document the question must retrieve. The region is part of its identity:
   * a global file carries the same attachmentId in all eight tables.
   */
  region: RegionCode;
  attachmentId: string;
  expectedFileName: string;
  /** Existing question, which switches the modal into edit mode. */
  currentQuestion?: string;
  /** Called after the question is saved, with the refreshed document. */
  onSaved: () => void;
}

const TestCaseModal = ({
  open,
  onClose,
  region,
  attachmentId,
  expectedFileName,
  currentQuestion,
  onSaved,
}: TestCaseModalProps) => {
  const [question, setQuestion] = useState("");
  const [preview, setPreview] = useState<Preview>();
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  /**
   * Identifies the preview the UI is currently interested in. A closure cannot
   * read the latest `question` after an await, so comparing against state would
   * always pass; this ref is the only reliable way to tell that the answer in
   * flight no longer belongs to what is on screen.
   */
  const previewToken = useRef(0);

  useEffect(() => {
    if (!open) return;
    setQuestion(currentQuestion ?? "");
    setPreview(undefined);
    setIsPreviewing(false);
    setIsSaving(false);
    setError("");
  }, [open, currentQuestion]);

  /** Editing the question invalidates any verdict already in flight for it. */
  const changeQuestion = (value: string) => {
    previewToken.current += 1;
    setQuestion(value);
    setPreview(undefined);
  };

  // A question is only useful if SAIGE actually cites the document, so the
  // answer has to be seen before it can be saved.
  const runPreview = async () => {
    const asked = question.trim();
    const token = (previewToken.current += 1);
    setIsPreviewing(true);
    setPreview(undefined);
    setError("");
    try {
      const result = await adminReportService.previewQuestion(
        asked,
        expectedFileName,
      );
      // Drop the answer if the question moved on while it was in flight,
      // otherwise Confirm would save new text against an old verdict.
      if (previewToken.current !== token) return;
      setPreview(result);
    } catch {
      if (previewToken.current !== token) return;
      setError("Could not run the question. Try again.");
    } finally {
      setIsPreviewing(false);
    }
  };

  const confirm = async () => {
    setIsSaving(true);
    setError("");
    try {
      await adminReportService.saveTestCase(
        region,
        attachmentId,
        question.trim(),
      );
      onSaved();
      onClose();
    } catch {
      // Stay open so the typed question is not lost.
      setError("Could not save the test case. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const canPreview = Boolean(question.trim()) && !isPreviewing;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={currentQuestion ? "Edit Test Case" : "Add Test Case"}
      width="640px"
      bodySpacingInset="400"
      /* The preview can be tall, so keep it inside the modal rather than
         growing the modal and scrolling the page behind it. */
      scrollContainer="modal"
    >
      <Column spacing="400">
        <Column spacing="100">
          <span className="admin-report__section-label">Expected document</span>
          <Text type="b300">{expectedFileName}</Text>
        </Column>

        <Input
          label="Question"
          value={question}
          onChange={changeQuestion}
          disabled={isPreviewing || isSaving}
          placeholder="What should SAIGE be able to answer from this document?"
          width="100%"
        />

        <Row widths="fit">
          <Button
            type="secondary"
            size="small"
            disabled={!canPreview}
            onClick={runPreview}
          >
            Test Question
          </Button>
        </Row>

        {error && (
          <Alert type="error" size="small">
            {error}
          </Alert>
        )}

        {isPreviewing && <LoadingSpinner />}

        {preview && <QuestionPreview preview={preview} />}
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            disabled={!preview || isSaving}
            onClick={confirm}
          >
            Confirm
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default TestCaseModal;
